import type {
    CreatureWalkingSpeedFoldInput,
    CreatureWalkingSpeedFoldResult,
    CreatureSpeedSource,
} from '../types';
import { applyCreatureSpeedFactorRows } from './creature-speed-factor';

/** Fold the ordinary walking-speed ledger before token, echo, jumping, or mount passes. */
export function foldCreatureWalkingSpeedSources(input: CreatureWalkingSpeedFoldInput): CreatureWalkingSpeedFoldResult {
    const sources: CreatureSpeedSource[] = input.sources.map(source => ({ ...source }));
    const inventoryIndexes = sources
        .map((source, index) => source.kind === 'inventory_equipped' ? index : -1)
        .filter(index => index >= 0);

    if (inventoryIndexes.length > 1) {
        let winnerIndex = inventoryIndexes[0] ?? 0;
        for (const index of inventoryIndexes.slice(1)) {
            if ((sources[index]?.value ?? 0) > (sources[winnerIndex]?.value ?? 0)) winnerIndex = index;
        }
        for (const index of inventoryIndexes) {
            if (index === winnerIndex) continue;
            const source = sources[index];
            if (source) sources[index] = { ...source, raw_value: source.value, value: 0, suppressed: true };
        }
    }

    const additiveTotal = sources.reduce((sum, source) => sum + source.value, 0);
    // Unclamped: carried factor deltas come off before the floor (see ft_speed_apply_factor_rows).
    const { total } = applyCreatureSpeedFactorRows(sources, input.dynamicBrace !== null ? Math.max(0, input.dynamicBrace) : additiveTotal, input.dynamicBrace !== null);
    const categoryTotals = new Map<string, number>();
    for (const source of sources) {
        const category = typeof source.category === 'string' ? source.category : source.kind;
        categoryTotals.set(category, (categoryTotals.get(category) ?? 0) + source.value);
    }
    const breakdown = Object.fromEntries(categoryTotals);
    return {
        total,
        base: 0,
        locked: input.dynamicBrace !== null,
        ...(sources.length ? { sources } : {}),
        ...(Object.keys(breakdown).length ? { breakdown } : {}),
    };
}

/** Same first-pass fold for flying, burrowing, hovering, and teleport. */
export const foldCreaturePlainSpeedSources = foldCreatureWalkingSpeedSources;
